import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { AxiosResponse } from 'axios';
import * as Hangul from 'hangul-js';
import moment, { Moment } from 'moment';
import { useNavigate } from 'react-router-dom';

import NavBar from 'components/navBar/NavBar';
import { BusinessCard, IBusinessCard } from './businessCard/businessCard';
import ExcelDownload from 'assets/icons/excelDownload.svg';
import SearchIcon from 'assets/icons/searchIcon.svg';
import ExceedingAlert from './exceedingAlert/exceedingAlert';
import './business-establishment-choice.scss';
import useApi from 'hooks/useApi';
import useLoadingStore from 'zustands/useLoadingStore';
import { IStoreItem } from './types';
import useModalStore from 'zustands/useAlertConfirmStore';
import CommonModal from 'common/CommonModal';
import DateRangeSelector from 'common/date-range-selector/date-range-selector';
import styleGuide from 'constants/styleGuide';
import useStoreStore from 'zustands/useStoreStore';
import useErrorLog from 'hooks/useErrorLog';
import { StatusType, useSubscribeStatus } from 'hooks/useSubscribe';
import useUserStore from 'zustands/useUserStore';

const DateArea = styled.div`
  display: flex;
  margin-top: 10%;
  justify-content: center;
`;
const BtnArea = styled.div`
  margin-top: 10%;
  width: 100%;
  text-align: center;
`;
const BtnDown = styled.button`
  padding: 3% 0;
  width: 40%;
  border-radius: 4px;
  background-color: ${styleGuide.palette.primary2};
`;
const BtnDownText = styled.strong`
  color: white;
`;

type Props = {};

const BusinessEstablishmentChoice: React.FC<Props> = () => {
  const navigate = useNavigate();

  const { setLoadingState } = useLoadingStore();
  const { setStoreInfo } = useStoreStore();
  const { setModalState } = useModalStore();
  const { setUserInfo, userType, ISP } = useUserStore();

  const { getSubscribeStatus, status } = useSubscribeStatus();
  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [searchValue, setSearchValue] = useState<string>(''); // 사업장 검색 입력 값
  const [store_list, setStoreList] = useState<IStoreItem[]>([]); // 사업장 리스트 데이터

  const [isVisibleExcelDownModal, setIsVisibleExcelDownModal] = useState<boolean>(false); // 엑셀 다운로드 모달 표시 여부
  const [excelDownStartDate, setExcelDownStartDate] = useState<Date>(
    moment(new Date(moment().year(), moment().month(), 1)).toDate(),
  ); // 엑셀다운로드 시작일
  const [excelDownEndDate, setExcelDownEndDate] = useState<Date>(moment().toDate()); // 엑셀다운로드 종료일

  // 사업장 데이터 패칭
  const fetchData = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/getallstorelist`);
      console.log('auth/getstorelist data', data);
      if (data.resultCode === 0) {
        if (data.resultData.stores.length > 0) {
          setStoreList(
            data.resultData.stores.map((store: any) => ({
              ACTIVATE_STATUS: store.ACTIVATE_STATUS,
              NAME: store.NAME,
              STORE_SEQ: store.STORE_SEQ,
              STORE_SEQ_NUMBER: store.STORE_SEQ1,
              emplist: store.emplist,
              workinglist: store.workinglist,
            })),
          );
        }
      } else {
        throw new Error('/getallstorelist');
      }
    } catch (err) {
      console.log('err', err);
      checkErrorLog(err, 'BusinessEstablishmentChoice fetchData');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 계정정보
  const fetchAccountInfo = async () => {
    try {
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/getMyPageInfo`);
      console.log('/auth/getMyPageInfo_data', data);

      if (data.resultCode === 0) {
        if (data.resultData.length > 0) {
          setUserInfo({
            userId: data.resultData[0].MobileNo,
            userBirthDate: moment(data.resultData[0].BIRTHDATE).toDate(),
            userName: data.resultData[0].NAME,
          });
        }
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/getMyPageInfo');
    } finally {
    }
  };

  // 사업주 비즈니스 구독권 체크
  const showNeedBusinessSubscribeModal = () => {
    // 매니저 통과
    if (userType === '0') return true;
    // 비즈니스 구독권 체크
    if (status.length === 0 || !status[0].pp_name.includes('비즈니스')) {
      setModalState({
        isModalShow: true,
        title: '비즈니스 구독권 미보유',
        desc: '샵솔 PC 버전은 비즈니스 플랜을 구독하신 뒤 이용하실 수 있습니다.',
        okBtnText: '구독권 보기',
        okCallback: () => {
          navigate('/payment');
        },
      });
      return false;
    }
    // 비즈니스지만 활성화 또는 구독 취소예정 상태가 아닌경우
    if (status[0].rss_status !== 'ACTIVE' && status[0].rss_status !== 'CANCEL_WAIT') {
      setModalState({
        isModalShow: true,
        title: '비즈니스 구독권 만료',
        desc: '이용중인 구독권이 만료되었습니다.',
        okBtnText: '구독권 보기',
        okCallback: () => {
          navigate('/payment');
        },
      });
      return false;
    }
    return true;
  };

  // 전체 사업장 출퇴근기록 다운받기 모달 표시 토글하기
  const toggleExcelDownVisible = useCallback(async () => {
    if (!isVisibleExcelDownModal) {
      if (showNeedBusinessSubscribeModal()) {
        setIsVisibleExcelDownModal(!isVisibleExcelDownModal);
      }
    } else {
      setIsVisibleExcelDownModal(!isVisibleExcelDownModal);
    }
  }, [status, isVisibleExcelDownModal]);

  // 전체 사업장 출퇴근기록 다운받기
  const downAllStoreCommuteExcel = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode(
        'get',
        `/auth/getadminmakeexcel?START_DATE=${moment(excelDownStartDate).format('YYYY-MM-DD')}&END_DATE=${moment(
          excelDownEndDate,
        ).format('YYYY-MM-DD')}`,
      );
      console.log('/getadminmakeexcel data', data);
      if (data.resultCode === 0) {
        toggleExcelDownVisible();
        setModalState({
          isModalShow: true,
          title: '출퇴근기록 다운로드',
          desc: '완료되었습니다.',
          closeCallback: () => {
            window.location = data.resultData.fileurl;
          },
        });
      } else {
        throw new Error('/getadminmakeexcel');
      }
    } catch (err) {
      checkErrorLog(err, 'BusinessEstablishmentChoice downAllStoreCommuteExcel');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 사업장 검색 시 필터 처리
  const searchStore = () => {
    let list = store_list;
    if (list.length > 0) {
      let search1 = searchValue;
      let search2 = Hangul.disassemble(search1).join('');
      const result = list.filter(function (item) {
        if (item.NAME !== undefined) {
          return item.NAME.includes(search1) || item.disassembled?.includes(search2);
        }
      });

      return searchValue == '' ? store_list : result;
    } else {
      return [];
    }
  };

  // 직원인 경우 해당 사업장 사업주의 비즈니스 구독권 여부 체크
  const checkOwnerByStoreSubscribeStatus = async (storeSeq: string) => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/checkStorePlan`, {
        STORE_SEQ: storeSeq,
      });
      if (data.resultCode === 0) {
        return true;
      } else if (data.resultCode === 1) {
        return setModalState({
          isModalShow: true,
          title: '구독권 미보유 사업장',
          desc: '샵솔 PC 버전은 비즈니스 플랜을 구독하신 뒤 이용하실 수 있습니다. 해당사업장의 사업주님에게 문의해 주세요',
        });
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/checkStorePlan');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
    return false;
  };

  // 사업장 클릭하여 입장
  const enterStoreFn = async (store: IBusinessCard) => {
    const completeEnterStore = () => {
      setStoreInfo({ storeSeq: store.storeSeq, storeName: store.storeName, storeSeqNumber: store.storeSeqNumber });
      navigate('/dashboard');
    };

    if (ISP) {
      completeEnterStore();
    } else if (store.storeStatus === 'disabled') {
      return setModalState({
        isModalShow: true,
        title: '비활성 사업장',
        desc: '해당 사업장은 비활성화 상태입니다. 잠금을 해제하시려면 비즈니스 플랜을 구독해 주세요.',
        okBtnText: '구독권 보기',
        okCallback: () => {
          navigate('/payment');
        },
      });
    } else {
      if (userType === '1') {
        if (showNeedBusinessSubscribeModal()) {
          completeEnterStore();
        }
      } else {
        if (await checkOwnerByStoreSubscribeStatus(store.storeSeq)) {
          completeEnterStore();
        }
      }
    }
  };

  // 사업장 추가 클릭, 구독권 체크
  const clickAddStore = async () => {
    if (showNeedBusinessSubscribeModal()) {
      navigate(`/business-establishment-setting`);
    }
  };

  const initFetch = async () => {
    await getSubscribeStatus();
    await fetchData();
    await fetchAccountInfo();
  };

  useEffect(() => {
    initFetch();
  }, []);

  const FilteredStoreBySearching = searchStore();

  return (
    <>
      <NavBar />
      <div className="businessEstablishmentChoice">
        <div className="pageHeader">사업장리스트</div>
        <div className="searchRow">
          <div className="searchWrapper">
            <div className="searchImageWrapper">
              <img src={SearchIcon} alt="사업장검색 아이콘" />
            </div>

            <input
              placeholder="사업장명을 입력하세요"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
              }}
              style={{
                color: searchValue !== '' ? 'black' : '',
              }}
            />
          </div>
          <button className="button" onClick={toggleExcelDownVisible}>
            <div className="buttonImage">
              <img src={ExcelDownload} alt="" />
            </div>
            <div className="grayButtonText">엑셀 다운로드</div>
          </button>
        </div>
        <div className="businessCardContainer">
          {FilteredStoreBySearching.map((store) => {
            return (
              <BusinessCard
                key={store.STORE_SEQ}
                storeSeq={store.STORE_SEQ}
                storeSeqNumber={store.STORE_SEQ_NUMBER}
                storeStatus={store.ACTIVATE_STATUS}
                storeName={store.NAME}
                employees={store.emplist}
                duty={store.workinglist}
                onClick={enterStoreFn}
              />
            );
          })}
          {userType === '1' && <ExceedingAlert onClick={clickAddStore} />}
        </div>
      </div>
      <CommonModal
        isVisible={isVisibleExcelDownModal}
        onClose={toggleExcelDownVisible}
        title={'전체사업장 출퇴근기록 엑셀 다운로드'}>
        <DateArea>
          <DateRangeSelector
            startDate={excelDownStartDate}
            setStartDate={setExcelDownStartDate}
            endDate={excelDownEndDate}
            setEndDate={setExcelDownEndDate}
          />
        </DateArea>
        <BtnArea>
          <BtnDown onClick={downAllStoreCommuteExcel}>
            <BtnDownText>다운받기</BtnDownText>
          </BtnDown>
        </BtnArea>
      </CommonModal>
    </>
  );
};

export default BusinessEstablishmentChoice;
